/**
 * Disponibilité midi / soir — port TypeScript de src/utils/availabilityUtils.js
 * et du filtrage produit de src/components/Restaurant.js (onProductUpdate /
 * checkProductWithAvailability). Comportement volontairement identique à la
 * tablette pour que les deux canaux montrent la même carte au même moment.
 *
 * Données :
 *  - Restaurant.settings.availabilityHours :
 *      { default: { lunch: { start, end }, diner: { start, end } },
 *        overrides: { "0"…"6" | "publicHoliday": { lunch, diner } } }
 *  - Product.availability : { always?: true, "<jour>-lunch"?: bool, "<jour>-diner"?: bool,
 *      "publicHoliday-lunch"?: bool, "publicHoliday-diner"?: bool }
 *    Un produit sans `availability` est toujours visible.
 *
 * Même logique côté serveur dans functions/src/lib/availability.ts (garder les
 * deux fichiers alignés ; là-bas l'horloge est convertie en Europe/Paris).
 */
import { isPublicHolidayToday } from "./publicHolidays";

export interface ServicePeriodHours {
  start: string; // "HH:MM"
  end: string; // "HH:MM"
}

export interface DayAvailabilityHours {
  lunch?: ServicePeriodHours;
  diner?: ServicePeriodHours;
}

export interface AvailabilityHours {
  default?: DayAvailabilityHours;
  overrides?: Record<string, DayAvailabilityHours>;
}

export type ServiceDay = number | "publicHoliday";

export interface AvailabilityPeriod {
  /** true = service du midi (lunch), false = service du soir (diner). */
  isDayTime: boolean;
  /** Jour de service (0 = dimanche … 6 = samedi, ou "publicHoliday"). */
  serviceDay: ServiceDay;
}

/** Horloge locale minimale : suffit au calcul de période, injectable en test. */
export interface LocalClock {
  weekDayIdx: number; // 0 = dimanche … 6 = samedi
  hour: number;
  minute: number;
}

export interface AvailabilityContext {
  period: AvailabilityPeriod;
  isPublicHoliday: boolean;
  /** Clé stable : ne change qu'au passage d'une frontière midi/soir ou de jour. */
  key: string;
}

/**
 * Fin de la « nuit » : avant cette heure, on est encore rattaché au service du
 * soir de la veille. Même frontière que le repli sans horaires (`hour <= 5`).
 */
const NIGHT_END_MINUTES = 6 * 60;

const timeToMinutes = (time?: string): number => {
  if (!time || typeof time !== "string") return 0;
  const [h, m] = time.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const previousDay = (weekDayIdx: number): number => (weekDayIdx + 6) % 7;

export const clockFromDate = (now: Date = new Date()): LocalClock => ({
  weekDayIdx: now.getDay(),
  hour: now.getHours(),
  minute: now.getMinutes(),
});

/**
 * Période de service courante (midi ou soir) et jour de service associé.
 * Gère un service du soir qui passe minuit : à 00:30 mardi avec un soir
 * 18:00-02:00, on est encore sur le soir de lundi.
 */
export const getAvailabilityPeriod = (
  availabilityHours: AvailabilityHours | null | undefined,
  isPublicHoliday: boolean,
  clock: LocalClock,
): AvailabilityPeriod => {
  const { weekDayIdx, hour } = clock;
  const holidayOr = (day: number): ServiceDay => (isPublicHoliday ? "publicHoliday" : day);

  // Repli sans horaires configurés (ou sans plage midi) : tout est midi entre
  // 6h et 17h, et les petites heures appartiennent au soir de la veille.
  const fallback = (): AvailabilityPeriod => {
    if (hour > 5 && hour < 17) return { isDayTime: true, serviceDay: weekDayIdx };
    if (hour <= 5) return { isDayTime: false, serviceDay: previousDay(weekDayIdx) };
    return { isDayTime: false, serviceDay: weekDayIdx };
  };

  if (!availabilityHours) return fallback();

  const currentMinutes = hour * 60 + clock.minute;
  const dayKey = isPublicHoliday ? "publicHoliday" : String(weekDayIdx);
  const todayHours = availabilityHours.overrides?.[dayKey] || availabilityHours.default;

  if (!todayHours?.lunch) return fallback();

  const lunchStart = timeToMinutes(todayHours.lunch.start);
  const lunchEnd = timeToMinutes(todayHours.lunch.end);
  const inLunch =
    lunchStart <= lunchEnd
      ? currentMinutes >= lunchStart && currentMinutes < lunchEnd
      : currentMinutes >= lunchStart || currentMinutes < lunchEnd;
  if (inLunch) return { isDayTime: true, serviceDay: holidayOr(weekDayIdx) };

  // Sans plage du soir, le service du soir ne commence jamais : la journée
  // reste « midi » (la tablette planterait ici, on préfère une carte du midi).
  const dinerStart = todayHours.diner ? timeToMinutes(todayHours.diner.start) : 24 * 60;
  const dinerEnd = todayHours.diner ? timeToMinutes(todayHours.diner.end) : 24 * 60;
  const dinerCrossesMidnight = dinerStart > dinerEnd;

  if (dinerCrossesMidnight) {
    if (currentMinutes >= dinerStart) return { isDayTime: false, serviceDay: holidayOr(weekDayIdx) };
  } else if (currentMinutes >= dinerStart && currentMinutes < dinerEnd) {
    return { isDayTime: false, serviceDay: holidayOr(weekDayIdx) };
  }

  // Soir de la veille qui a passé minuit ?
  const prevDayKey = isPublicHoliday ? "publicHoliday" : String(previousDay(weekDayIdx));
  const prevDayHours = availabilityHours.overrides?.[prevDayKey] || availabilityHours.default;
  if (prevDayHours?.diner) {
    const prevDinerStart = timeToMinutes(prevDayHours.diner.start);
    const prevDinerEnd = timeToMinutes(prevDayHours.diner.end);
    if (prevDinerStart > prevDinerEnd && currentMinutes < prevDinerEnd) {
      return { isDayTime: false, serviceDay: holidayOr(previousDay(weekDayIdx)) };
    }
  }

  // Hors de toute plage : tant que le soir n'a pas commencé, c'est la carte du
  // midi (le matin avant l'ouverture, ou le creux entre les deux services),
  // sauf les petites heures qui restent rattachées au soir de la veille.
  if (currentMinutes < dinerStart) {
    if (currentMinutes < NIGHT_END_MINUTES) {
      return { isDayTime: false, serviceDay: holidayOr(previousDay(weekDayIdx)) };
    }
    return { isDayTime: true, serviceDay: holidayOr(weekDayIdx) };
  }
  return { isDayTime: false, serviceDay: holidayOr(weekDayIdx) };
};

/**
 * Un produit est visible si : pas de champ `availability`, ou `always`, ou la
 * clé `<jour>-lunch|diner` (jour férié : `publicHoliday-lunch|diner`) est vraie.
 */
export const isProductAvailable = (
  availability: Record<string, boolean> | null | undefined,
  period: AvailabilityPeriod,
  isPublicHoliday: boolean,
): boolean => {
  if (!availability) return true;
  if (availability.always) return true;
  const slot = period.isDayTime ? "lunch" : "diner";
  if (isPublicHoliday) return !!availability[`publicHoliday-${slot}`];
  return !!availability[`${period.serviceDay}-${slot}`];
};

export const getAvailabilityContext = (
  availabilityHours: AvailabilityHours | null | undefined,
  now: Date = new Date(),
): AvailabilityContext => {
  const isPublicHoliday = isPublicHolidayToday(now);
  const period = getAvailabilityPeriod(availabilityHours, isPublicHoliday, clockFromDate(now));
  return {
    period,
    isPublicHoliday,
    key: `${isPublicHoliday ? "ph" : "d"}-${period.serviceDay}-${period.isDayTime ? "lunch" : "diner"}`,
  };
};

export const filterAvailableProducts = <T extends { availability?: Record<string, boolean> | null }>(
  productsById: Map<string, T>,
  ctx: AvailabilityContext,
): Map<string, T> => {
  const out = new Map<string, T>();
  for (const [id, p] of productsById) {
    if (isProductAvailable(p.availability, ctx.period, ctx.isPublicHoliday)) out.set(id, p);
  }
  return out;
};
